import {
    GridColumnVisibilityModel,
    GridFilterModel,
    GridRowId,
    GridSortItem,
    GridSortModel,
    getGridDateOperators,
    GridFilterItem,
    GridColDef,
    GridSingleSelectColDef,
} from "@mui/x-data-grid-premium";
import { IEntityPageRequest } from "@shared/mirage/core/contracts/IEntityPageRequest";
import { Filter, Order, Where } from "@shared/mirage/entityTable/Filter";
import { BaseColumnBinding, EntityBindingColumns, Relations } from "@shared/mirage/metadata/BaseEntityRecordBinding";
import { ITableState } from "./EntityTable";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import cellWithPopover from "./components/cellWithPopover";
import _ from "lodash";
import { EntityFieldAccessor } from "./EntityFieldAccessor";
import DateFormatter from "@shared/mirage/helpers/DateFormatter";
import InputBetweenDates from "./components/InputBetweenDates";
import NumberFormatter from "@shared/mirage/helpers/NumberFormatter";
import React from "react";
import { IUserTablePreferences } from "@shared/mirage/services/IUserPreferencesService";
export class EntityHelper {
    public static preparePageRequest(tableState: ITableState, filter: Filter, columns: GridColDef[], fetchAllColumns?: boolean, customColumnsOrder?: { [x: string]: string }) {
        const pageRequest: IEntityPageRequest = {
            select: EntityHelper.prepareColumnsObject(columns, tableState.columnVisibilityModel, fetchAllColumns),
            pageSize: tableState.pageSize,
            offset: EntityHelper.calculateOffset(tableState.pageIndex, tableState.pageSize),
            filter: filter,
            order: EntityHelper.prepareSortingObject(tableState.sortModel, customColumnsOrder),
        };

        return pageRequest;
    }
    public static prepareFilterObject(filterModel: GridFilterModel, columns?: readonly GridColDef[]) {
        const operator = filterModel.logicOperator?.toUpperCase() || "AND";
        const items = filterModel.items;
        const whereItems = items.map((item) => {
            const columnOperator = item.operator || "=";
            let value = item.value;
            if (item.operator === "isEmpty" || item.operator === "isNotEmpty") {
                value = true;
            }
            return {
                [item.field]: {
                    [columnOperator]: value,
                },
            };
        });
        const quickFilterValues: { [x: string]: { contains: string } }[] = [];
        if (columns?.length && filterModel.quickFilterValues?.length) {
            columns.forEach((col) => {
                if (col.type !== "boolean" && col.type !== "date") {
                    filterModel.quickFilterValues?.forEach((quickFilterValue) => {
                        quickFilterValues.push({
                            [col.field]: {
                                contains: quickFilterValue as string,
                            },
                        });
                    });
                }
            });
        }
        let where = {} as Where;
        if (whereItems.length === 0) {
            if (quickFilterValues.length) {
                where = { OR: quickFilterValues } as Where;
            }
        } else {
            where = { [operator]: whereItems } as Where;
        }
        const filter = { parts: [], where } as Filter;
        return filter;
    }
    public static prepareSortingObject(sortItems: GridSortItem[], customColumnsOrder?: { [x: string]: string }) {
        const sortObject: Order = {} as Order;
        (sortItems || []).forEach((sortItem) => {
            if (customColumnsOrder && customColumnsOrder[sortItem.field]) {
                sortObject[sortItem.field] = `${customColumnsOrder[sortItem.field]} ${sortItem.sort?.toUpperCase()}`;
                return;
            }
            sortObject[sortItem.field] = sortItem.sort?.toLowerCase();
        });
        return sortObject;
    }
    private static prepareColumnsObject(columns: GridColDef[], columnVisibilityModel?: GridColumnVisibilityModel, fetchAllColumns?: boolean) {
        const obj = {} as { [columnName: string]: boolean };
        if (fetchAllColumns) {
            return obj;
        }
        const idColumnExists = columns.some((el) => el.field.toUpperCase() === "ID");
        const baseColumns = ["createdTime", "lastModifiedTime", "createdByUserId", "lastModifiedByUserId"];
        columns.forEach((column) => {
            if (columnVisibilityModel && columnVisibilityModel[column.field] === false && !baseColumns.includes(column.field)) {
                return;
            }
            obj[column.field] = true;
        });
        if (!idColumnExists) {
            // If ID column is not present, add it to the select (fix for public portal)
            obj["id"] = true;
        }
        return obj;
    }
    public static calculateOffset(pageIndex: number, pageSize: number) {
        return pageIndex > 0 ? pageIndex * pageSize : 0;
    }
    public static normaliseColumnsWidth = (columns: readonly GridColDef[]) => {
        return columns.map((column) => {
            if (!column.flex && !column.width) {
                column.flex = 1;
            }
            return column;
        });
    };
    public static createSortModel(order: Order | undefined, sortModel: GridSortModel | undefined): GridSortItem[] {
        if (!order || !Object.keys(order).length) {
            if (sortModel) {
                return sortModel;
            } else {
                return [];
            }
        }
        const sortItems: GridSortItem[] = Object.keys(order).map((key) => {
            return {
                field: key,
                sort: order[key]?.toLowerCase() == "desc" ? "desc" : "asc",
            };
        });
        return sortItems;
    }
    public static getPageIndex(initial: IEntityPageRequest | undefined): number {
        if (!initial) {
            return 0;
        }
        if (typeof initial.offset === "undefined") {
            return 0;
        }
        if (initial.offset === 0) {
            return 0;
        }
        if (initial.pageSize === 0) {
            return 0;
        }
        return initial.offset / initial.pageSize;
    }

    public static setLookupTypeForRelationColumns(columns: readonly GridColDef[], relations: Relations | undefined) {
        const relationColumns = relations ? Object.keys(relations) : [];
        columns.forEach((column) => {
            if (relationColumns.includes(column.field)) {
                column.type = "lookup";
            }
        });
    }

    private static columnIsSingleSelectType(column: GridColDef): column is GridSingleSelectColDef {
        return column.type === "singleSelect";
    }

    public static setColumnPropsBasedOnType(columns: readonly GridColDef[], metaColumns?: EntityBindingColumns<any>) {
        return columns.map((column) => {
            if (this.columnIsSingleSelectType(column) && metaColumns) {
                column.valueOptions = column.valueOptions ? column.valueOptions : metaColumns[column.field]?.options || [];
            }
            if (column.type === "number") {
                column.headerAlign = "left";
            }
            if (column.type === "lookup") {
                column.valueGetter = (params) => {
                    if (!params.row[`${params.field}_relatedRecord`]?.id) {
                        return null;
                    }
                    return { id: params.row[`${params.field}_relatedRecord`]?.id, name: params.row[`${params.field}_relatedRecord`]?.name } || null;
                };
                column.valueFormatter = (params) => {
                    return params.value?.name || "";
                };
            }
            if (column.type === "date") {
                column.valueFormatter = (params) => DateFormatter.formatToDate(params.value);
                column.filterOperators = [
                    ...getGridDateOperators(),
                    {
                        label: "between",
                        value: "between",
                        getApplyFilterFn: (filterItem: GridFilterItem) => {
                            if (!Array.isArray(filterItem.value) || filterItem.value.length !== 2) {
                                return null;
                            }
                            if (filterItem.value[0] == null || filterItem.value[1] == null) {
                                return null;
                            }

                            return ({ value }) => {
                                return value !== null && filterItem.value[0] <= value && value <= filterItem.value[1];
                            };
                        },
                        InputComponent: InputBetweenDates,
                    },
                ];
            }
            if (column.type === "dateTime") {
                column.valueFormatter = (params) => DateFormatter.formatToDateTimeWithSeconds(params.value);
            }
            if (column.type === "time") {
                column.valueFormatter = (params) => DateFormatter.formatTimeAmPm(params.value);
            }
            if (column.type === "currency") {
                column.valueFormatter = (params) => NumberFormatter.formatToGBP(params.value);
                column.align = "right";
            }
            if (column.type === "meters") {
                column.valueFormatter = (params) => NumberFormatter.formatMeters(params.value);
            }
            if (column.type === "area-square-meters") {
                column.valueFormatter = (params) => NumberFormatter.formatSquareMeters(params.value);
                column.align = "right";
            }
            if (column.type === "percent") {
                column.valueFormatter = (params) => NumberFormatter.formatPercent(params.value);
                column.align = "right";
            }
            if (column.type === "phone") {
                column.valueParser = (v) => (v ? Number(v) : null);
            }
            return column;
        });
    }

    public static setNotesColumn(columns: GridColDef[], onTextAreaSubmit?: (id: GridRowId, field: string, newVal: any) => Promise<void>, isEdit?: boolean) {
        columns.forEach((c, idx) => {
            if (c.type === "notes") {
                columns[idx] = { ...columns[idx], ...cellWithPopover(onTextAreaSubmit, isEdit) };
            }
        });
    }

    public static setRequiredColumns(columns: readonly GridColDef[], metaColumns?: EntityBindingColumns<unknown>) {
        if (!metaColumns || !columns) {
            return;
        }
        columns.forEach((column) => {
            const columnBinding: BaseColumnBinding = metaColumns[column.field as keyof EntityBindingColumns<unknown>];
            if (columnBinding?.isRequired) {
                const existingClassName = column.cellClassName || "";
                column.cellClassName = `${existingClassName} EntityTable__cell--required`.trim();
            }
        });
    }
    public static replaceFilterForColumnField(field: string, operator: string, value: any, apiRef: React.MutableRefObject<GridApiPremium>) {
        const currentFilters = apiRef.current.state.filter.filterModel.items;
        const filtersWithoutEditedColumn = currentFilters.filter((item) => item.field !== field);
        const newFilter = {
            field,
            id: `${field}-${operator}`,
            operator,
            value: value,
        };
        apiRef.current.upsertFilterItems([...filtersWithoutEditedColumn, newFilter]);
    }

    public static setBaseColumns(columns: GridColDef<any, any, any>[], disableFilterOnBaseColumn?: boolean) {
        const baseColumns = [
            {
                field: "createdTime",
                headerName: "Created Time",
                editable: false,
                flex: 0.5,
                type: "dateTime",
                filterable: !disableFilterOnBaseColumn,
            },
            {
                field: "lastModifiedTime",
                headerName: "Last Modified Time",
                editable: false,
                flex: 0.5,
                type: "dateTime",
                filterable: !disableFilterOnBaseColumn,
            },
            { field: "createdByUserId", headerName: "Created By", editable: false, flex: 0.5, type: "lookup", filterable: !disableFilterOnBaseColumn },
            { field: "lastModifiedByUserId", headerName: "Last Modified By", editable: false, flex: 0.5, type: "lookup", filterable: !disableFilterOnBaseColumn },
        ] as GridColDef<any, any, any>[];

        columns.forEach((column) => {
            baseColumns.forEach((baseColumn, idx) => {
                if (column.field === baseColumn.field) {
                    baseColumns.splice(idx, 1); // Remove column if that was declared initially in ET
                }
            });
        });

        columns.push(...baseColumns);
    }
    public static setColumnTypes(columns: GridColDef[], bindingColumns: EntityBindingColumns<any>) {
        columns.forEach((column) => {
            if (bindingColumns[column.field]?.contentType) {
                if (bindingColumns[column.field].contentType === "CHECKBOX") {
                    column.type = "boolean";
                } else if (bindingColumns[column.field].contentType === "DATE") {
                    column.type = "date";
                } else if (bindingColumns[column.field].contentType === "DATETIME") {
                    column.type = "dateTime";
                } else if (bindingColumns[column.field].contentType === "PICKLIST") {
                    column.type = "singleSelect";
                }
            }
        });
    }

    public static revertColumnsToUserPreferences(columns: GridColDef[], uiSettings: IUserTablePreferences) {
        const storedColumns = uiSettings.columns || [];
        columns.forEach((column) => {
            const columnFromStorage = storedColumns.find((c: { field: string }) => c.field === column.field);
            if (columnFromStorage) {
                if (columnFromStorage.width) {
                    column.width = columnFromStorage.width;
                    column.flex = undefined;
                }
            }
        });
        const columnOrder = uiSettings.columnOrder || [];
        columnOrder.forEach((field, idx) => {
            const column = columns.find((col) => col.field === field);
            if (column) {
                columns.splice(columns.indexOf(column), 1);
                columns.splice(idx, 0, column);
            }
        });
    }

    public static formatColumns(
        propsColumns: readonly GridColDef[],
        excludeBaseColumns?: boolean,
        setActionsColumn?: (columns: GridColDef<any, any, any>[]) => void,
        setPanelToggleColumn?: (columns: GridColDef<any, any, any>[]) => void,
        disableFilterOnBaseColumn?: boolean,
        bindingColumns?: EntityBindingColumns<any>,
        orderColumns?: GridSortItem[],
        applyFilterOnHeader?: (columns: GridColDef[]) => void,
        uiSettings?: IUserTablePreferences | null
    ) {
        const columns = EntityHelper.normaliseColumnsWidth(propsColumns);
        !excludeBaseColumns && EntityHelper.setBaseColumns(columns, disableFilterOnBaseColumn);
        EntityHelper.AddOrderColumnsToColumns(columns, orderColumns, bindingColumns);
        bindingColumns && EntityHelper.setColumnTypes(columns, bindingColumns);
        setActionsColumn && setActionsColumn(columns);
        applyFilterOnHeader && applyFilterOnHeader(columns);
        setPanelToggleColumn && setPanelToggleColumn(columns); // Keep this column first
        EntityHelper.setColumnPropsBasedOnType(columns, bindingColumns);
        uiSettings && EntityHelper.revertColumnsToUserPreferences(columns, uiSettings);
        return columns;
    }

    private static setColumnDefinitionsFromBinding(columns: GridColDef[], bindingColumns: EntityBindingColumns<any>) {
        columns.forEach((column) => {
            const bindingColumn = bindingColumns[column.field];
            if (bindingColumn) {
                EntityHelper.getColumnDefByBinding(bindingColumn);
            }
        });
    }

    private static AddOrderColumnsToColumns(columns: GridColDef[], order?: GridSortItem[], bindingColumns?: EntityBindingColumns<any>) {
        if (!order?.length) {
            return;
        }
        const orderColumnsNotInColumns = EntityHelper.getOrderColumnsNotInColumnsList(columns, order);
        const orderColumns = orderColumnsNotInColumns.map((oc) => {
            const bindingColumn = bindingColumns?.[oc.field];
            if (bindingColumn) {
                return { field: oc.field, headerName: bindingColumn.label, ...EntityHelper.getColumnDefByBinding(bindingColumn) };
            }
            return { field: oc.field, headerName: oc.field, type: "string" };
        });
        columns.push(...orderColumns);
        return columns;
    }

    private static getOrderColumnsNotInColumnsList(columns: readonly GridColDef[], order?: GridSortItem[]) {
        if (!order?.length) {
            return [];
        }
        return order.filter((oc) => !columns.find((c) => c.field === oc.field));
    }

    public static prepareColumnVisibilityModel(
        columns: readonly GridColDef[],
        columnVisibilityModel?: GridColumnVisibilityModel,
        excludeBaseColumns?: boolean,
        order?: GridSortItem[]
    ) {
        const baseColumnVisibility = {
            createdTime: false,
            lastModifiedTime: false,
            createdByUserId: false,
            lastModifiedByUserId: false,
        };
        const orderColumnsNotInColumns = EntityHelper.getOrderColumnsNotInColumnsList(columns, order);
        const orderColumnsVisibility = orderColumnsNotInColumns.reduce((acc, oc) => ({ ...acc, [oc.field]: false }), {});

        return {
            ...(excludeBaseColumns ? {} : baseColumnVisibility),
            ...(columnVisibilityModel || {}),
            ...orderColumnsVisibility,
        };
    }

    public static getColumnDefByBinding(col: BaseColumnBinding): Partial<GridColDef<any, any, any>> {
        if (col.contentType == "DATE") {
            return { type: "date", flex: 0.3 };
        }
        if (col.contentType == "DATETIME") {
            return { type: "dateTime", flex: 0.3 };
        }
        if (col.contentType == "TIME") {
            return { type: "time", flex: 0.3 };
        }

        if (col.contentType == "CHECKBOX") {
            return { type: "boolean", flex: 0.1 };
        }
        if (col.contentType == "NUMBER") {
            return { flex: 0.3 };
        }
        return {};
    }
    public static toggleLimitToUserFilter(pageRequest: IEntityPageRequest, officerFieldName: string, limitToUser: number | undefined) {
        const pr = _.cloneDeep(pageRequest);
        if (!pr.filter.where) {
            pr.filter.where = { AND: [] as Where<any>[] };
        }
        const rootWhere = pr.filter.where.AND as Where<any>[];

        const alreadyFilteredIndex = rootWhere.findIndex((e: any) => e[officerFieldName]);
        if (limitToUser) {
            if (alreadyFilteredIndex === -1) {
                rootWhere.push({ [officerFieldName]: { equals: limitToUser } });
            }
        } else {
            if (alreadyFilteredIndex >= 0) {
                rootWhere.splice(alreadyFilteredIndex, 1);
            }
        }
        return pr;
    }
}
export const toGridColumn = (col: BaseColumnBinding) => {
    return {
        field: col.fieldName,
        headerName: col.label,
        type: col.contentType.toLowerCase() === "checkbox" ? "boolean" : col.contentType.toLowerCase(),
    } as GridColDef;
};
export const fromRelatedProperty = (prop: string, header: string) => {
    return {
        field: prop,
        headerName: header,
        valueGetter: (params) => {
            return _.get(params.row, prop);
        },
    } as GridColDef;
};
export const fromExtendedRecord = <T>(property: (object: T) => void, columns: EntityBindingColumns<any>) => {
    const accessor = new EntityFieldAccessor<T>();
    const { propertyPath, column } = accessor.getFieldPath(property);
    if (propertyPath && column) {
        const binding = columns[column];
        if (binding) {
            return {
                field: propertyPath,
                headerName: binding.label,
                type: binding.contentType.toLowerCase(),
                valueGetter: (params) => {
                    return _.get(params.row, propertyPath);
                },
            } as GridColDef;
        }
    }
    return null;
};
